/**
 * Keyframes for the voice surfaces (fill-by-voice strip, builder memo pill).
 *
 * The engine ships no stylesheet: hosts compile its Tailwind class strings,
 * but keyframes cannot travel that way. The few the voice UI needs are
 * injected once per document, the first time a voice component mounts.
 * Every animation has a reduced-motion form that keeps the state change
 * legible without the movement.
 */

const STYLE_ID = "eaco-voice-styles";

const CSS = `
@keyframes ev-rise {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes ev-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes ev-shimmer {
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
}
@keyframes ev-breathe {
  0%, 100% { transform: scale(1); opacity: .55; }
  50% { transform: scale(1.35); opacity: .15; }
}
@keyframes ev-tick {
  0% { transform: scale(.6); opacity: 0; }
  60% { transform: scale(1.08); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
.ev-rise { animation: ev-rise .42s cubic-bezier(.16,1,.3,1) both; }
.ev-fade { animation: ev-fade .3s ease-out both; }
.ev-tick { animation: ev-tick .5s cubic-bezier(.16,1,.3,1) both; }
.ev-shimmer::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,.55) 50%, transparent 100%);
  animation: ev-shimmer 1.4s ease-in-out infinite;
}
.ev-breathe { animation: ev-breathe 2.6s ease-in-out infinite; }
.ev-halo {
  transform: scale(calc(1 + var(--voice-level, 0) * .6));
  opacity: calc(.1 + var(--voice-level, 0) * .28);
  transition: transform 90ms linear, opacity 90ms linear;
}
@media (prefers-reduced-motion: reduce) {
  .ev-rise { animation: ev-fade .25s ease-out both; }
  .ev-tick { animation: ev-fade .25s ease-out both; }
  .ev-shimmer::after { animation: none; opacity: .35; }
  .ev-breathe { animation: none; }
  .ev-halo { transform: none; opacity: calc(.1 + var(--voice-level, 0) * .3); transition: opacity 160ms linear; }
}
`;

/** Inject the voice keyframes once; safe to call on every mount. */
export function ensureVoiceStyles(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
}

/** Whether the viewer asked for reduced motion (false when unknown). */
export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
